/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type { AvoidanceApi, OptimizeForApi } from 'types/api/route/routeOptimizationJob'

// The job endpoints echo back only id, status and stops, so a run's column and date would be lost on unmount.
export interface OptimizationJobContext {
  routeId: string
  // The board column the run was fired from: a service date, or `MASTER`.
  gridId: string
  // The day it was optimized against; for a master run, the soonest day it fans out to.
  serviceDate: string
  isMasterRun: boolean
  // Set when its comparison was accepted, so a notification can say the run is already spent.
  appliedAt?: number
  // What the run was submitted with. The job read echoes neither back, so only this browser can say.
  optimizeFor?: OptimizeForApi
  avoid?: AvoidanceApi[]
}

interface PersistentOptimizationJobsState {
  byJobId: Record<string, OptimizationJobContext>
}

const initialState: PersistentOptimizationJobsState = { byJobId: {} }

export const PERSISTENT_OPTIMIZATION_JOBS_KEY = 'persistentOptimizationJobs' as const

// Bounded so the persisted blob cannot grow without limit; only recent runs are still reopenable.
const MAX_REMEMBERED_JOBS = 25

const persistentOptimizationJobsSlice = createSlice({
  name: PERSISTENT_OPTIMIZATION_JOBS_KEY,
  initialState,
  reducers: {
    rememberOptimizationJob: (state, action: PayloadAction<{ jobId: string; context: OptimizationJobContext }>) => {
      const { jobId, context } = action.payload
      state.byJobId[jobId] = context
      const jobIds = Object.keys(state.byJobId)
      jobIds.slice(0, Math.max(0, jobIds.length - MAX_REMEMBERED_JOBS)).forEach(stale => {
        delete state.byJobId[stale]
      })
    },
    markOptimizationApplied: (state, action: PayloadAction<{ jobId: string; appliedAt: number }>) => {
      const entry = state.byJobId[action.payload.jobId]
      if (entry) entry.appliedAt = action.payload.appliedAt
    },
  },
})

export const { rememberOptimizationJob, markOptimizationApplied } = persistentOptimizationJobsSlice.actions

export interface PersistentOptimizationJobsRootSlice {
  [PERSISTENT_OPTIMIZATION_JOBS_KEY]?: PersistentOptimizationJobsState
}

export const selectOptimizationJobContext =
  (jobId: string | null) =>
  (state: PersistentOptimizationJobsRootSlice): OptimizationJobContext | null =>
    jobId ? state[PERSISTENT_OPTIMIZATION_JOBS_KEY]?.byJobId[jobId] ?? null : null

export default persistentOptimizationJobsSlice.reducer
